import { get, post } from 'Api';
import { persistCompetitionEntry, getPersistedCompetitionEntries, clearVerifiedPersistedCompetitionEntries } from 'Helpers/Acquisition/persisted-competition-entries';
import GeneralUtils from 'Helpers/GeneralUtils';
import mergeCompetitionEntries from 'Helpers/Acquisition/competition-entries';

export const FETCH_COMPETITIONS = 'frontend/competition/FETCH_COMPETITIONS';
export const SET_COMPETITION_ENTRIES = 'frontend/competition/SET_COMPETITION_ENTRIES';
export const ADD_COMPETITION_ENTRY = 'frontend/competition/ADD_COMPETITION_ENTRY';

/**
 * Builds the competitionEntries slice from the fetched competitions, the user's entries from the
 * API and local storage. API entry status takes precedence; local entries cover unverified giveaways.
 * @param {Array} competitions
 * @param {Array} apiEntries the signed-in user's entries ({ competitionId, entryDate })
 */
function setCompetitionEntries(competitions = [], apiEntries = []) {
  return {
    type: SET_COMPETITION_ENTRIES,
    payload: mergeCompetitionEntries({
      persisted: getPersistedCompetitionEntries(),
      competitions: Array.isArray(competitions) ? competitions : [],
      apiEntries: Array.isArray(apiEntries) ? apiEntries : [],
    }),
  };
}

/**
 * Rebuilds the entries slice from local storage only, dropping any API-derived
 * (account-specific) entries. Unverified (device-local) entries are preserved.
 * Call on logout.
 */
export function resetCompetitionEntries() {
  clearVerifiedPersistedCompetitionEntries();
  return setCompetitionEntries([]);
}

/**
 * Enters the current user into a competition.
 * @param {Object} params
 * @param {string} params.competitionId
 * @param {string} params.email
 * @param {boolean} params.joinNewsletter
 * @param {boolean} params.accountRequired - verified (account-based) competitions
 * are not persisted locally; they are derived from the API on next fetch.
 */
export function enterCompetition({
  competitionId, email, joinNewsletter = false, accountRequired = false,
} = {}) {
  return async (dispatch) => {
    const { response, data } = await post(dispatch, '/competition/enter', {
      competitionId,
      email,
      joinNewsletter,
      snowplowId: GeneralUtils.getSnowplowDomainUserId(),
      snowplowSessionId: GeneralUtils.getSnowplowSessionId(),
    });

    if (!response.ok) return { entered: false, errorType: data?.errorType };

    // Persist locally so entry survives a page refresh before fetchCompetitions runs.
    // accountRequired is stored so logout can selectively clear only verified entries.
    persistCompetitionEntry(competitionId, accountRequired);

    dispatch({
      type: ADD_COMPETITION_ENTRY, payload: { competitionId, entryDate: new Date().toISOString() },
    });

    return { entered: true };
  };
}

/**
 * Fetches competitions for the current locale and rebuilds the entries slice.
 * The list is the same for every visitor in a country and is cached at the edge. What the signed-in
 * user has entered is a separate request, made only when there is a competition to have entered.
 */
export function fetchCompetitions() {
  return async (dispatch, getState) => {
    const language = window.locale || 'en';
    // The token is the user and session. If it changes while a request is in flight (a logout, a
    // login as someone else, a 401) what came back may belong to the account that left, so the
    // whole fetch is dropped. Not every way the session changes clears the entries (a 401 does
    // not, nor does an admin logging in as a user), so they are cleared here, and fetched again
    // if someone is still signed in.
    const { authenticated, token } = getState().auth;
    const stale = () => getState().auth.token !== token;
    const abandon = () => {
      dispatch(resetCompetitionEntries());

      return getState().auth.authenticated ? dispatch(fetchCompetitions()) : null;
    };

    const { response, data } = await get(dispatch, `/competition/all/${language}`);

    if (stale()) return abandon();

    const competitions = response.ok ? (data || []) : [];

    dispatch({ type: FETCH_COMPETITIONS, payload: competitions });

    let apiEntries = [];

    if (competitions.length && authenticated) {
      let entries = null;

      try {
        entries = await get(dispatch, '/user/competition-entries');
      } catch {
        // Best effort: carry on with the list and what is stored on the device
      }

      if (stale()) return abandon();

      if (entries?.response?.ok) apiEntries = entries.data;
    }

    return dispatch(setCompetitionEntries(competitions, apiEntries));
  };
}
